import { OrderCard } from 'src/components/Orders/OrderCards/orderCard';
import { Order } from 'src/utils/interfaces/interfaces';
import { useNavigate } from 'react-router-dom';
import {
  useGetOrdersListMutation,
  useSearchOrdersMutation,
} from 'src/services/orders/orders';
import { Input } from 'src/components/UI/Forms/Inputs';
import { useState, useEffect, useRef } from 'react';
import {
  APPROVED,
  COMPLETED,
  DRAFT,
  IN_TRANSIT,
  READY,
} from 'src/constants/statusConstants';
import { useTranslation } from 'react-i18next';
import { Pagination, Tabs, Tab, Chip, Button, Modal, ModalContent, ModalHeader, ModalBody } from '@heroui/react';
import i18n from 'src/i18n';
import OrdersTable from '../ordersListView';
import { TbReportSearch } from 'react-icons/tb';
import Reports from 'src/pages/reports/reports';

export interface IOrdersProps { }

type OrderType = 'completed' | 'ongoing' | 'other';
interface Meta {
  count: number;
  limit: number;
  page: number;
}

interface OrdersState {
  completed: Order[];
  ongoing: Order[];
  draft: Order[];
  other: Order[];
}

interface MetaState {
  completed: Meta;
  ongoing: Meta;
  other: Meta;
}

export function EnforcementOrders() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [orders, setOrders] = useState<OrdersState>({
    completed: [],
    ongoing: [],
    draft: [],
    other: [],
  });

  const [meta, setMeta] = useState<MetaState>({
    completed: { count: 0, limit: 20, page: 1 },
    ongoing: { count: 0, limit: 20, page: 1 },
    other: { count: 0, limit: 20, page: 1 },
  });

  const [isListView, setIsListView] = useState(() => {
    const savedIsListView = window.localStorage.getItem('isListView');
    return savedIsListView !== null ? JSON.parse(savedIsListView) : true;
  });

  const [type, setTypeView] = useState<OrderType>(() => {
    const savedType = window.localStorage.getItem('activeTab') as OrderType;
    return savedType && ['completed', 'ongoing', 'other'].includes(savedType)
      ? savedType
      : 'ongoing';
  });

  const [activeOrder, setActiveOrder] = useState<string | null>(() => {
    return window.localStorage.getItem('activeOrder');
  });

  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    return window.localStorage.getItem('searchTerm') || '';
  });

  const [getOrdersList] = useGetOrdersListMutation({});

  const [searchOrders] = useSearchOrdersMutation();

  const containerRef = useRef<HTMLDivElement>(null);
  const mountHandled = useRef(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [selectedMapEntity, setSelectedMapEntity] = useState<string>('all');
  const [isReportOpen, setIsReportOpen] = useState(false);

  const fetchOrders = async (
    orderType: OrderType,
    page: number,
    search: string = '',
    entity?: string
  ) => {
    setIsOrderLoading(true);
    try {
      const status =
        orderType === 'ongoing'
          ? IN_TRANSIT
          : orderType === 'completed'
            ? COMPLETED
            : READY;
      const params: any = {
        status,
        limit: meta[orderType].limit,
        page,
      };
      if (entity) {
        params.entity = entity;
      }
      const response = await getOrdersList(params).unwrap();

      const { response: orders, meta: responseMeta } = response;

      if (orders && orders.length > 0) {
        setOrders((prevData) => ({
          ...prevData,
          [orderType]: orders,
        }));
        setMeta((prevMeta) => ({
          ...prevMeta,
          [orderType]: {
            ...responseMeta,
            limit: Number(responseMeta.limit),
          },
        }));
      } else {
        setOrders((prevData) => ({
          ...prevData,
          [orderType]: [],
        }));
      }
    } catch (error) {
    } finally {
      setIsOrderLoading(false);
    }
  };

  const fetchSearchOrders = async (
    orderType: OrderType,
    search: string,
    entity?: string
  ) => {
    setIsOrderLoading(true);
    try {
      const status =
        orderType === 'ongoing'
          ? IN_TRANSIT
          : orderType === 'completed'
            ? COMPLETED
            : READY;
      const params: any = { status, word: search };
      if (entity) {
        params.entity = entity;
      }
      const response = await searchOrders(params).unwrap();
      if (response && response.length > 0) {
        setOrders((prevData) => ({
          ...prevData,
          [orderType]: response,
        }));
      } else {
        setOrders((prevData) => ({
          ...prevData,
          [orderType]: [],
        }));
      }
    } catch (error) {
    } finally {
      setIsOrderLoading(false);
    }
  };

  const handleEntityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const entityValue = event.target.value;
    setSelectedEntity(entityValue);
    setAnchorEl(null);
    if (searchTerm) {
      fetchSearchOrders(
        type,
        searchTerm,
        entityValue !== 'all' ? entityValue : undefined
      );
    } else {
      fetchOrders(
        type,
        meta[type].page,
        searchTerm,
        entityValue !== 'all' ? entityValue : undefined
      );
    }
  };

  const resetPreservedData = () => {
    window.localStorage.removeItem('activeTab');
    window.localStorage.removeItem('activeOrder');
    window.localStorage.removeItem('activePage');
    window.localStorage.removeItem('scrollPosition');
  };

  useEffect(() => {
  }, [orders]);

  useEffect(() => {
    if (containerRef.current) {
      const savedScrollPosition = window.localStorage.getItem('scrollPosition');
      if (savedScrollPosition) {
        containerRef.current.scrollTo(0, parseInt(savedScrollPosition, 10));
      }
    }
  }, [type, meta]);

  // Handle restoring page from sessionStorage on mount
  useEffect(() => {
    const fromOrdersPage = sessionStorage.getItem('fromOrdersPage');
    if (fromOrdersPage) {
      const savedPage = window.localStorage.getItem('activePage');
      if (savedPage) {
        setMeta((prev) => ({
          ...prev,
          [type]: { ...prev[type], page: Number(savedPage) },
        }));
      }
      sessionStorage.removeItem('fromOrdersPage');
    } else {
      resetPreservedData();
    }
    // Mark that mount logic has run - the reactive useEffect below will handle the actual fetch
    mountHandled.current = true;
  }, []);

  const onChangeSearch = (searchString: string) => {
    setSearchTerm(searchString);
    if (searchString) {
      window.localStorage.setItem('searchTerm', searchString);
      fetchSearchOrders(
        type,
        searchString,
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    } else {
      window.localStorage.removeItem('searchTerm');
      fetchOrders(
        type,
        meta[type].page,
        '',
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    }
  };

  const ListView = () => {
    window.localStorage.removeItem('activeOrder');
    setActiveOrder(null);
    setIsListView((prevIsListView: boolean) => {
      const newIsListView = !prevIsListView;
      window.localStorage.setItem('isListView', JSON.stringify(newIsListView));
      return newIsListView;
    });
  };

  const onTabChange = (newType: string | number) => {
    setTypeView(newType as OrderType);
    setMeta((prev) => ({
      ...prev,
      [newType as OrderType]: {
        ...prev[newType as OrderType],
        page: 1,
      },
    }));
    resetPreservedData();
    // The reactive useEffect will handle fetching when type/meta changes
  };

  useEffect(() => {
    if (searchTerm) {
      fetchSearchOrders(
        type,
        searchTerm,
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    } else {
      fetchOrders(
        type,
        meta[type].page,
        '',
        selectedEntity !== 'all' ? selectedEntity : undefined
      );
    }
  }, [type, meta[type]?.page, searchTerm, selectedEntity]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }, [type]);

  const handlePageClick = (newPage: number) => {
    resetPreservedData();
    setMeta((prev) => ({
      ...prev,
      [type]: { ...prev[type], page: newPage },
    }));
    // The reactive useEffect will handle fetching when meta[type].page changes
  };

  const totalPageCount = Math.ceil(meta[type].count / meta[type].limit);

  const handleOrderClick = (order: Order) => {
    sessionStorage.setItem('fromOrdersPage', 'true');
    window.localStorage.setItem('activeTab', type);
    window.localStorage.setItem('activeOrder', order._id);
    window.localStorage.setItem('activePage', meta[type].page.toString());
    window.localStorage.setItem(
      'scrollPosition',
      containerRef.current?.scrollTop.toString() || '0'
    );
    setActiveOrder(order._id);
    navigate(`/orders/${order._id}`);
  };

  return (
    <div className="flex w-full mt-2 px-6" style={{ backgroundColor: 'var(--bg-base)' }}>
      <div
        className="w-full h-screen overflow-y-auto overflow-x-hidden"
        style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-main)' }}
        ref={containerRef}
      >
        {/* Sticky Header and Tabs */}
        <div className="sticky top-0 z-40" style={{ backgroundColor: 'var(--bg-base)' }}>
          {/* Header */}
          <div className="pt-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl" style={{ color: 'var(--text-main)' }}>{t('orders_page.title')}</h3>
              </div>
              <div>
                <div className="flex justify-end mt-4">
                  <Input
                    type="text"
                    placeholder={t('orders_page.search_textbox')}
                    inputClassName="rounded-lg border border-[var(--border-light)] placeholder:text-[var(--text-muted)]"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChangeSearch(e.target.value)
                    }
                    iconUrl="/assets/icons/search.svg"
                    defaultValue={searchTerm}
                  ></Input>
                </div>
              </div>
            </div>
          </div>
          {/* Tabs with icons and counts */}
          <div className="flex items-center w-full flex-row" style={{ borderBottom: '1px solid var(--border-light)' }}>
            <Tabs
              aria-label="Orders Tabs"
              variant="underlined"
              selectedKey={type}
              onSelectionChange={(key) => onTabChange(key)}
              classNames={{
                tabList:
                  'gap-10 w-full relative rounded-none p-0 overflow-hidden',
                cursor: 'w-full bg-[var(--color-primary)]',
                tab: 'max-w-fit px-1 h-14',
                tabContent:
                  'group-data-[selected=true]:text-[var(--color-primary)] text-[var(--text-muted)] transition-all duration-300',
              }}
            >
              <Tab
                key="other"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                      ? 'space-x-reverse space-x-2'
                      : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_page.ready_orders')}
                    </span>
                    {type === 'other' &&
                      !searchTerm &&
                      meta.other.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="text-md shadow-lg mx-4"
                          style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}
                        >
                          {meta.other.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>

              <Tab
                key="ongoing"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                      ? 'space-x-reverse space-x-2'
                      : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_page.ongoing_orders')}
                    </span>
                    {type === 'ongoing' &&
                      !searchTerm &&
                      meta.ongoing.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="text-md shadow-lg"
                          style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}
                        >
                          {meta.ongoing.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>

              <Tab
                key="completed"
                title={
                  <div
                    className={`flex items-center ${i18n.language === 'ar'
                      ? 'space-x-reverse space-x-2'
                      : 'space-x-2'
                      }`}
                  >
                    <span className="text-medium font-semibold">
                      {t('orders_page.completed_orders')}
                    </span>
                    {type === 'completed' &&
                      !searchTerm &&
                      meta.completed.count > 0 && (
                        <Chip
                          size="md"
                          variant="solid"
                          className="text-md shadow-lg"
                          style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}
                        >
                          {meta.completed.count}
                        </Chip>
                      )}
                  </div>
                }
              ></Tab>
            </Tabs>
            <div className="flex gap-2 mb-4">
              <Button
                variant="solid"
                className="rounded-md p-2 transition-colors duration-200"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--text-invert)',
                }}
                onPress={() => setIsReportOpen(true)}
                startContent={<TbReportSearch className="w-5 h-5" />}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
              >
                {t('orders_page.report')}
              </Button>
              {/* <Button
                variant="outlined"
                className={`text-white flex items-center relative ${
                  isLastLocationLoading ? "opacity-60 cursor-not-allowed pointer-events-none" : ""
                }`}
                onClick={() => !isLastLocationLoading && setIsModalOpen(true)}
              >
                <span className="relative inline-flex items-center justify-center">
                  <span className="h-2 w-2 bg-danger rounded-full"></span>
                  <span className="absolute h-4 w-4 border border-danger rounded-full animate-blink"></span>
                </span>
                <span className="text-white capitalize ml-3">Live</span>
              </Button> */}

              {/* <Button
                variant="solid"
                startContent={<AddIcon />}
                className="bg-red-300 rounded-md p-2 text-white"
                onClick={() => setIsCreateOrderOpen(true)}
              >
                Create Order
              </Button> */}

              {/* <Button
                variant="solid"
                isIconOnly
                onClick={(event) => setAnchorEl(event.currentTarget)}
                className=" bg-bluePrimary-100/[0.9] rounded-md"
              >
                <FilterAltIcon
                  className="text-white"
                  titleAccess="Filter"
                  fontSize="small"
                />
              </Button> */}
              {/* <Button
                variant="solid"
                isIconOnly
                onClick={ListView}
                className=" bg-bluePrimary-100/[0.9] rounded-md p-0"
              >
                {isListView ? (
                  <GridViewIcon
                    className="text-white"
                    titleAccess="Card View"
                    fontSize="small"
                  />
                ) : (
                  <ListIcon
                    className="text-white"
                    titleAccess="List View"
                    fontSize="small"
                  />
                )}
              </Button> */}

              {/* <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                sx={{
                  "& .MuiPaper-root": {
                    backgroundColor: "#243B54",
                    padding: "0px 15px",
                    color: "#FFF",
                  },
                }}
              >
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="entity"
                    name="entity"
                    value={selectedEntity}
                    onChange={handleEntityChange}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio sx={{ color: "white" }} />}
                      label="All"
                    />
                    <FormControlLabel
                      value="federal_customs"
                      control={<Radio sx={{ color: "white" }} />}
                      label="FCA"
                    />
                    <FormControlLabel
                      value="dubai_customs"
                      control={<Radio sx={{ color: "white" }} />}
                      label="Dubai"
                    />
                  </RadioGroup>
                </FormControl>
              </Menu> */}
            </div>
          </div>
        </div>

        {/* Content Area with Padding */}
        <div>
          {/* Filter and List View Buttons */}

          {/* Tab Content */}
          {type === 'other' && (
            <div className="transition-all duration-300">
              {/* Content for 'other' tab */}
              {orders.other && orders.other.length > 0 ? (
                isListView && type === 'other' ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.other}
                      type={APPROVED}
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.other.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => {
                          if (order.status === APPROVED)
                            handleOrderClick(order);
                        }}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-main)' }}>
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center" style={{ color: 'var(--text-main)' }}>
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}
          {type === 'ongoing' && (
            <div>
              {/* Content for 'ongoing' tab */}
              {orders.ongoing && orders.ongoing.length > 0 ? (
                isListView ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.ongoing}
                      type="ongoing"
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.ongoing.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => handleOrderClick(order)}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-main)' }}>
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center" style={{ color: 'var(--text-main)' }}>
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}
          {type === 'completed' && (
            <div>
              {/* Content for 'completed' tab */}
              {orders.completed && orders.completed.length > 0 ? (
                isListView && type === 'completed' ? (
                  <div className="mt-6">
                    <OrdersTable
                      orders={orders.completed}
                      type="completed"
                      onRowClick={handleOrderClick}
                      activeOrder={activeOrder}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 mt-6 mb-32">
                    {orders.completed.map((order, count) => (
                      <OrderCard
                        order={order}
                        activeOrder={activeOrder}
                        key={count}
                        onClick={() => handleOrderClick(order)}
                      />
                    ))}
                  </div>
                )
              ) : (
                <div className="px-6" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-main)' }}>
                  <div className="flex flex-col items-center py-8 pb-4 mt-48">
                    <h3 className="mt-10 mb-2 text-2xl tracking-wide text-center" style={{ color: 'var(--text-main)' }}>
                      {t('orders_page.no_orders')}
                    </h3>
                    <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {!searchTerm && orders[type].length > 0 && (
            <div
              className={`fixed bottom-0 w-full flex justify-center p-4 z-10`}
              style={{ backgroundColor: 'var(--bg-base)', borderTop: '1px solid var(--border-light)' }}
            >
              <Pagination
                variant="flat"
                key={type}
                color="primary"
                size={'md'}
                showShadow={false}
                showControls
                total={totalPageCount}
                initialPage={1}
                page={meta[type].page}
                onChange={(newPage) => handlePageClick(newPage)}
                isDisabled={isOrderLoading}
                classNames={{
                  next: `${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'}`,
                  prev: `${i18n.language === 'ar' ? 'scale-x-[-1]' : 'scale-x-1'}`,
                  item: 'm-1 shadow-sm',
                }}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        size="full"
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        backdrop="blur"
        classNames={{
          base: 'p-0',
          closeButton:
            'm-1 p-2',
        }}
        style={{ backgroundColor: 'var(--bg-base)' } as React.CSSProperties}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="font-bold text-2xl mt-1 ps-8" style={{ color: 'var(--text-main)' }} />
              <ModalBody className="overflow-y-auto" style={{ backgroundColor: 'var(--bg-base)' }}>
                <Reports />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}

export default EnforcementOrders;
